/**
 * Owner: Masa Bayu
 * File: author-profile-data.ts
 * Date:Time: 2026-10-17 14:05
 * Description: Author profile surface demo model — presentation only, ≠ entitlement.
 */

import { STUDIO_ENTRY } from './author-production-identity';

export type ProfileIcon = {
  readonly icon: string;
  readonly tint: string;
  readonly ink: string;
};

export type ProfileNavItem = ProfileIcon & {
  readonly id: string;
  readonly label: string;
  readonly path: string;
};

export type ProfileStat = ProfileIcon & {
  readonly label: string;
  readonly value: string;
};

export type ProfileActivity = ProfileIcon & {
  readonly text: string;
  readonly time: string;
};

export type ProfileQuickLink = ProfileIcon & {
  readonly title: string;
  readonly sub: string;
  readonly path: string;
};

export type ContinueReadingItem = {
  readonly title: string;
  readonly author: string;
  readonly percent: number;
  readonly meta: string;
  readonly tone: string;
};

export type LibraryBook = {
  readonly title: string;
  readonly author: string;
  readonly status: string;
  readonly tone: string;
};

export type AuthoredBook = {
  readonly title: string;
  readonly date: string;
  readonly reads: string;
  readonly rating: string;
  readonly price: string;
  readonly tone: string;
};

export const PROFILE_PERSONA = {
  name: 'Alya Rahman',
  initials: 'AR',
  handle: '@alyarahman',
  site: 'qxebook.com/alyarahman',
  bio: 'Writer, dreamer and lifelong learner. I write about self development, wellness and the art of living a meaningful life.',
  joined: 'Joined Mar 2024',
  visibility: 'Visible',
  completion: 85,
  checklist: [
    'Basic Information',
    'Profile Photo',
    'Bio & About',
    'Interests & Genres',
    'Author Information',
  ],
  goalPercent: 60,
  goalLabel: '12/20 books',
  salesTotal: 'RM 1,248.00',
  salesDelta: '22%',
  booksSold: '312',
  booksSoldDelta: '18%',
  averagePrice: 'RM 15.90',
  averagePriceDelta: '12%',
  royalties: 'RM 624.00',
  royaltiesDelta: '24%',
  spark: [4, 6, 5, 8, 7, 10, 9, 12, 11, 14],
} as const;

export const PROFILE_NAV: readonly ProfileNavItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    tint: '#eef2ff',
    ink: '#4f46e5',
    icon: 'M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z',
  },
  {
    id: 'profile',
    label: 'Profile Overview',
    path: '/author/profile',
    tint: '#fce7f3',
    ink: '#db2777',
    icon: 'M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zM4 20c0-3.3 3.6-5 8-5s8 1.7 8 5',
  },
  {
    id: 'reader',
    label: 'Reader Dashboard',
    path: '/library',
    tint: '#ecfeff',
    ink: '#0891b2',
    icon: 'M12 5a7 7 0 0 0-7 7v7h14v-7a7 7 0 0 0-7-7zM12 5v14M9 9h.01M15 9h.01',
  },
  {
    id: 'studio',
    label: 'Author Studio',
    path: '/author',
    tint: '#fdf4ff',
    ink: '#a21caf',
    icon: 'M4 20l4-1 11-11a2 2 0 0 0-3-3L5 16zM13 6l3 3',
  },
  {
    id: 'library',
    label: 'My Library',
    path: '/library',
    tint: '#fff7ed',
    ink: '#c2410c',
    icon: 'M5 4h4v16H5zM10 4h4v16h-4zM17 5l3 1-4 14-3-1',
  },
  {
    id: 'books',
    label: 'My Books',
    path: '/my-books',
    tint: '#f0fdf4',
    ink: '#15803d',
    icon: 'M6 3h9l3 3v15H6zM15 3v3h3M9 11h6M9 15h6',
  },
  {
    id: 'activity',
    label: 'Reading Activity',
    path: '/author/analytics',
    tint: '#fef2f2',
    ink: '#dc2626',
    icon: 'M4 19h16M7 16v-5M11 16V8M15 16v-3M19 16V5',
  },
  {
    id: 'sales',
    label: 'Sales & Earnings',
    path: '/author/sales',
    tint: '#f5f3ff',
    ink: '#7c3aed',
    icon: 'M12 3v18M15 6H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H8',
  },
  {
    id: 'community',
    label: 'Reviews & Community',
    path: '/circle',
    tint: '#e0f2fe',
    ink: '#0284c7',
    icon: 'M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5zM9 11h.01M13 11h.01M17 11h.01',
  },
  {
    id: 'account',
    label: 'Account & Preferences',
    path: '/account',
    tint: '#f1f5f9',
    ink: '#475569',
    icon: 'M12 15a3 3 0 1 0-3-3 3 3 0 0 0 3 3zM19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.2 3h-4l-.4 2.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2l.4 2.7h4l.4-2.7a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5A7 7 0 0 0 19 12z',
  },
];

export const QUICK_STATS: readonly ProfileStat[] = [
  {
    label: 'Books Read',
    value: '12',
    tint: '#e0f2fe',
    ink: '#0284c7',
    icon: 'M12 6c-2-1.5-4.5-2-8-2v14c3.5 0 6 .5 8 2 2-1.5 4.5-2 8-2V4c-3.5 0-6 .5-8 2zM12 6v14',
  },
  {
    label: 'Reviews Given',
    value: '8',
    tint: '#fce7f3',
    ink: '#db2777',
    icon: 'M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8-4.2-4.1 5.9-.9z',
  },
  {
    label: 'Reading Time',
    value: '24h 30m',
    tint: '#f1f5f9',
    ink: '#475569',
    icon: 'M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9zM12 7v5l3 2',
  },
  {
    label: 'Books Published',
    value: '2',
    tint: '#ccfbf1',
    ink: '#0f766e',
    icon: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  },
  {
    label: 'Followers',
    value: '1.2k',
    tint: '#ede9fe',
    ink: '#6d28d9',
    icon: 'M9 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3zM2 19c0-2.8 3-4 7-4s7 1.2 7 4M17 8a3 3 0 1 1-2 5.7M22 19c0-2.2-1.8-3.4-4-3.8',
  },
  {
    label: 'Avg. Rating',
    value: '4.6',
    tint: '#fff7ed',
    ink: '#c2410c',
    icon: 'M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8-4.2-4.1 5.9-.9z',
  },
];

export const RECENT_ACTIVITY: readonly ProfileActivity[] = [
  {
    text: 'Finished reading The Currency of Calm',
    time: '2 days ago',
    tint: '#ccfbf1',
    ink: '#0f766e',
    icon: 'M6 3h9l3 3v15H6zM9 11h6M9 15h4',
  },
  {
    text: 'Added Dunes of Qamar to your library',
    time: '3 days ago',
    tint: '#ede9fe',
    ink: '#6d28d9',
    icon: 'M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z',
  },
  {
    text: 'Reviewed Small Engines',
    time: '5 days ago',
    tint: '#fff7ed',
    ink: '#c2410c',
    icon: 'M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8-4.2-4.1 5.9-.9z',
  },
  {
    text: 'Started reading Kokoro Paths',
    time: '5 days ago',
    tint: '#e0f2fe',
    ink: '#0284c7',
    icon: 'M10 3.5v5.2L5.2 18a2 2 0 0 0 1.8 2.9h10a2 2 0 0 0 1.8-2.9L14 8.7V3.5M8.7 3.5h6.6',
  },
  {
    text: 'Published new book The Better You',
    time: '1 week ago',
    tint: '#fce7f3',
    ink: '#db2777',
    icon: 'M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7zM9 12l2 2 4-4',
  },
];

export const QUICK_LINKS: readonly ProfileQuickLink[] = [
  {
    title: 'QXEditor',
    sub: 'Import PDF → QXW → QXP',
    path: STUDIO_ENTRY,
    tint: '#eff6ff',
    ink: '#1d4ed8',
    icon: 'M12 4v12m0 0l-4-4m4 4l4-4M5 20h14',
  },
  {
    title: 'My Profile',
    sub: 'Manage your profile and public information',
    path: '/author/profile',
    tint: '#fce7f3',
    ink: '#db2777',
    icon: 'M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zM4 20c0-3.3 3.6-5 8-5s8 1.7 8 5',
  },
  {
    title: 'My Books',
    sub: 'Manage your published books',
    path: '/my-books',
    tint: '#ede9fe',
    ink: '#6d28d9',
    icon: 'M6 3h9l3 3v15H6zM9 11h6M9 15h6',
  },
  {
    title: 'Reading Activity',
    sub: 'Track your reading progress',
    path: '/author/analytics',
    tint: '#fdf4ff',
    ink: '#a21caf',
    icon: 'M12 6c-2-1.5-4.5-2-8-2v14c3.5 0 6 .5 8 2 2-1.5 4.5-2 8-2V4c-3.5 0-6 .5-8 2zM12 6v14',
  },
  {
    title: 'Sales & Earnings',
    sub: 'View your sales and royalties',
    path: '/author/sales',
    tint: '#ccfbf1',
    ink: '#0f766e',
    icon: 'M12 3v18M15 6H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H8',
  },
  {
    title: 'Account & Preferences',
    sub: 'Manage your account settings',
    path: '/account',
    tint: '#f1f5f9',
    ink: '#475569',
    icon: 'M12 15a3 3 0 1 0-3-3 3 3 0 0 0 3 3zM19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.2 3h-4l-.4 2.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 2 1.2l.4 2.7h4l.4-2.7a7 7 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5A7 7 0 0 0 19 12z',
  },
];

export const CONTINUE_READING: readonly ContinueReadingItem[] = [
  {
    title: 'River Atlas',
    author: 'M. Okonkwo',
    percent: 62,
    meta: 'Chapter 4 · 2h 15m left',
    tone: '#f5e9d4',
  },
  {
    title: 'The Midnight Meridian',
    author: 'H. Vasquez',
    percent: 28,
    meta: 'Chapter 6 · 1h 20m left',
    tone: '#12203c',
  },
  {
    title: 'Dunes of Qamar',
    author: 'F. Anwar',
    percent: 0,
    meta: 'Chapter 1 · 6h 42m left',
    tone: '#7a3b12',
  },
  {
    title: 'Kokoro Paths',
    author: 'K. Yamada',
    percent: 76,
    meta: 'Chapter 12 · 2h 8m left',
    tone: '#f7dfe7',
  },
];

export const READING_STAT_TILES: readonly ProfileStat[] = [
  {
    label: 'Books Completed',
    value: '4',
    tint: '#ccfbf1',
    ink: '#0f766e',
    icon: 'M12 6c-2-1.5-4.5-2-8-2v14c3.5 0 6 .5 8 2 2-1.5 4.5-2 8-2V4c-3.5 0-6 .5-8 2zM12 6v14',
  },
  {
    label: 'Currently Reading',
    value: '3',
    tint: '#fce7f3',
    ink: '#db2777',
    icon: 'M12 6c-2-1.5-4.5-2-8-2v14c3.5 0 6 .5 8 2 2-1.5 4.5-2 8-2V4c-3.5 0-6 .5-8 2zM8 10h.01M8 14h.01',
  },
  {
    label: 'Reading Streak',
    value: '7 days',
    tint: '#fff7ed',
    ink: '#ea580c',
    icon: 'M12 21c-3 0-5-2-5-5 0-3 2-4 3-7 2 2 2 3 2 3s1-1 1-3c2 2 4 4 4 7 0 3-2 5-5 5z',
  },
  {
    label: 'Total Reading Time',
    value: '24h 30m',
    tint: '#ede9fe',
    ink: '#6d28d9',
    icon: 'M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9zM12 7v5l3 2',
  },
];

export const LIBRARY_BOOKS: readonly LibraryBook[] = [
  { title: 'River Atlas', author: 'M. Okonkwo', status: 'Read · 62%', tone: '#f5e9d4' },
  { title: 'Dunes of Qamar', author: 'F. Anwar', status: 'Reading · 28%', tone: '#7a3b12' },
  { title: 'Kokoro Paths', author: 'K. Yamada', status: 'Read · 76%', tone: '#f7dfe7' },
  { title: 'The Midnight Meridian', author: 'H. Vasquez', status: 'Unread', tone: '#12203c' },
  { title: 'Solar Habit', author: 'C. Moreno', status: 'Unread', tone: '#fdf3c7' },
  { title: 'The Quiet Compass', author: 'M. Reyes', status: 'Completed', tone: '#0e3a46' },
];

export const AUTHORED_BOOKS: readonly AuthoredBook[] = [
  {
    title: 'The Better You',
    date: 'Published · 12 Mar 2025',
    reads: '1.2k',
    rating: '4.6',
    price: 'RM 12.90',
    tone: '#7a3b12',
  },
  {
    title: 'Mindful Living',
    date: 'Published · 5 Jan 2025',
    reads: '845',
    rating: '4.8',
    price: 'RM 9.90',
    tone: '#12203c',
  },
];

export const LIBRARY_FILTERS = [
  'All Books',
  'Currently Reading',
  'Unread',
  'Completed',
  'Favorites',
] as const;

export const DRAFT_BOOKS = [
  { title: 'Untitled Memoir', date: 'Draft · edited 2 days ago', tone: '#334155' },
] as const;

/** Builds an SVG polyline `points` string scaled to a 100×36 viewBox. */
export function sparkPoints(spark: readonly number[]): string {
  const max = Math.max(...spark);
  return spark
    .map((value, index) => {
      const x = (index / (spark.length - 1)) * 100;
      const y = 34 - (value / max) * 30;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}
